import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from "react";
import PropTypes from "prop-types";

import ConfirmDialog from "components/modals/ConfirmDialog";

const NoteEditingContext = createContext(null);

/**
 * Holds which note card in a list is being edited, so that only one card at a
 * time mounts an editable editor. Switching away from an edit with unsaved
 * changes asks before discarding them.
 */
export function NoteEditingProvider({ children }) {
  const [editingKey, setEditingKey] = useState(null);
  const [pendingKey, setPendingKey] = useState(null);
  // Read only when another card asks to edit, so a ref keeps the open edit's
  // keystrokes from re-rendering the whole list.
  const isDirty = useRef(false);

  // A card that unmounts while editing, such as one whose note was deleted,
  // must not leave its key behind, or the next Edit elsewhere would ask to
  // discard changes in an editor that no longer exists. The check against the
  // current key keeps a card that lost the edit from clearing its successor.
  const releaseEdit = useCallback(key => {
    setEditingKey(current => (current === key ? null : current));
  }, []);

  const value = useMemo(() => {
    const switchTo = key => {
      isDirty.current = false;
      setEditingKey(key);
    };
    return {
      editingKey,
      requestEdit: key => {
        if (editingKey !== null && editingKey !== key && isDirty.current) {
          setPendingKey(key);
          return;
        }
        switchTo(key);
      },
      endEdit: () => switchTo(null),
      releaseEdit,
      setDirty: dirty => {
        isDirty.current = dirty;
      },
    };
  }, [editingKey, releaseEdit]);

  const confirmDiscard = () => {
    isDirty.current = false;
    setEditingKey(pendingKey);
    setPendingKey(null);
  };

  return (
    <NoteEditingContext.Provider value={value}>
      {children}
      <ConfirmDialog
        open={pendingKey !== null}
        onOpenChange={open => !open && setPendingKey(null)}
        title="Discard unsaved changes?"
        description="The note you are editing has changes that have not been saved."
        cancelText="Keep editing"
        confirmText="Discard changes"
        confirmVariant="destructive"
        onConfirm={confirmDiscard}
      />
    </NoteEditingContext.Provider>
  );
}

NoteEditingProvider.propTypes = {
  children: PropTypes.node,
};

export function useNoteEditing() {
  const context = useContext(NoteEditingContext);
  if (!context) {
    throw new Error("useNoteEditing must be used inside a NoteEditingProvider");
  }
  return context;
}
